import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { AspectMode, FrameRateMatchMode } from "@hybrid-iptv/player";

/**
 * How the viewer wants the picture drawn, set from the player's gear menu.
 *
 * ASPECT is a preference about their screen, not about one film, so it is
 * remembered: a 4:3 panel or a TV that over-scans is a property of the room.
 * SPEED belongs to what is playing and resets with it, which is what every
 * other player does — nobody wants last night's 1.5× on this morning's film.
 */
export interface PlayerSettingsStore {
  aspect: AspectMode;
  setAspect: (aspect: AspectMode) => void;
  rate: number;
  setRate: (rate: number) => void;
  /**
   * Leaving the app with a video playing floats it (Back on the first screen,
   * Home, recents). OFF by default — a tester asked for picture-in-picture to be
   * optional (2026-09-25). The player's picture-in-picture button still works
   * either way: pressing it is asking.
   */
  pipOnLeave: boolean;
  setPipOnLeave: (pipOnLeave: boolean) => void;
  /**
   * Tunnelled video (APK): smoother 1080i/60 on weak TV chips, where the app's
   * own frame handling fell behind (projector, 2026-09-27: 9 of 63 frames shown
   * on a 1080i channel). OFF until proven on real devices.
   */
  tunneling: boolean;
  setTunneling: (tunneling: boolean) => void;
  /**
   * Auto frame-rate matching (APK, G6): the screen switches to the video's frame rate.
   * OFF by default, and only ever the viewer's choice (owner, 2026-10-20: "settings options
   * not automatic"; the screen goes black for a moment when it switches).
   */
  frameRateMatch: FrameRateMatchMode;
  setFrameRateMatch: (frameRateMatch: FrameRateMatchMode) => void;
  /** Audio delay (APK, G7), ms from -500 to +500, positive delays the sound. 0 by default. */
  audioDelayMs: number;
  setAudioDelayMs: (audioDelayMs: number) => void;
  /**
   * Pause and rewind live TV (APK): the channel is saved to a buffer of up to
   * 60 minutes while it plays. ON by default (ADR 2026-09-27).
   */
  timeshift: boolean;
  setTimeshift: (timeshift: boolean) => void;
  /** The drive the buffer is kept on, an id from the DVR's volume list; unset is this device. */
  timeshiftVolumeId: string | undefined;
  setTimeshiftVolumeId: (volumeId: string | undefined) => void;
}

export const usePlayerSettings = create<PlayerSettingsStore>()(
  persist(
    (set) => ({
      aspect: "fit",
      setAspect: (aspect) => set({ aspect }),
      rate: 1,
      setRate: (rate) => set({ rate }),
      pipOnLeave: false,
      setPipOnLeave: (pipOnLeave) => set({ pipOnLeave }),
      tunneling: false,
      setTunneling: (tunneling) => set({ tunneling }),
      frameRateMatch: "off",
      setFrameRateMatch: (frameRateMatch) => set({ frameRateMatch }),
      audioDelayMs: 0,
      setAudioDelayMs: (audioDelayMs) => set({ audioDelayMs: Math.max(-500, Math.min(500, Math.round(audioDelayMs))) }),
      timeshift: true,
      setTimeshift: (timeshift) => set({ timeshift }),
      timeshiftVolumeId: undefined,
      setTimeshiftVolumeId: (timeshiftVolumeId) => set({ timeshiftVolumeId }),
    }),
    {
      name: "slique-player-settings",
      partialize: (state) => ({
        aspect: state.aspect,
        pipOnLeave: state.pipOnLeave,
        tunneling: state.tunneling,
        frameRateMatch: state.frameRateMatch,
        audioDelayMs: state.audioDelayMs,
        timeshift: state.timeshift,
        timeshiftVolumeId: state.timeshiftVolumeId,
      }),
    },
  ),
);
